import { DateTime } from "luxon";

export type DateInputType = "datetime-local" | "time";

export default class DatetimeHelper {
    static dateTimeStringFormat = "yyyy-MM-dd HH:mm:ss";

    static convertToLuxon(isoString: string | null | undefined) {
        //TODO this function should in the future handle all timezone conversions
        return isoString ? DateTime.fromISO(isoString) : null;
    }

    static convertDateTimeStringsToLuxon(
        dateTimeString: string | null | undefined
    ) {
        if (!dateTimeString) {
            return null;
        }

        //E.g 2025-06-23 18:00:00
        return DateTime.fromFormat(dateTimeString, this.dateTimeStringFormat);
    }

    static tryFromMultipleFormats = (
        date: string | null | undefined,
        formatsToTry: string[] = [
            "yyyy-MM-dd HH:mm",
            "yyyy-MM-dd'T'HH:mm",
            DatetimeHelper.dateTimeStringFormat,
            "yyyy-MM-dd", // optionally include more formats
            "yyyy-MM-dd'T'HH:mm:ssZZ", // ISO-like fallback if needed
        ]
    ): DateTime | null => {
        if (!date) return null;

        const timezone = window.timezone || "UTC";

        let luxonDate: DateTime | null = null;

        for (const format of formatsToTry) {
            const parsed = DateTime.fromFormat(date, format, {
                zone: timezone,
            });
            if (parsed.isValid) {
                luxonDate = parsed;
                break;
            }
        }

        // Final fallback for ISO strings or native date strings
        if (!luxonDate || !luxonDate.isValid) {
            const isoParsed = DateTime.fromISO(date, { zone: timezone });
            if (isoParsed.isValid) {
                luxonDate = isoParsed;
            }
        }

        if (!luxonDate || !luxonDate.isValid) {
            console.warn(
                "Invalid date passed to getVehicleRunTimeForParams:",
                date
            );
            return null;
        }

        return luxonDate;
    };

    static getDatetimeFormatForInputType(inputType: DateInputType) {
        switch (inputType) {
            case "datetime-local":
                return "yyyy-MM-dd'T'HH:mm";
            case "time":
                return "HH:mm";
            default:
                return "yyyy-MM-dd'T'HH:mm";
        }
    }

    static toDateTimeInput(dateTime: DateTime, inputType: DateInputType) {
        return dateTime.toFormat(this.getDatetimeFormatForInputType(inputType));
    }

    static toLocaleDateTime(isoString: string | null | undefined) {
        if (typeof isoString !== "string") {
            return "";
        }

        return DateTime.fromISO(isoString).toFormat("dd/MM/yyyy HH:mm");
    }

    static toLocalDate(isoString: string | null | undefined) {
        if (typeof isoString !== "string") {
            return "";
        }

        return DateTime.fromISO(isoString).toFormat("dd/MM/yyyy");
    }

    static utcWithoutOffsetToLocaleTimeOnly(
        isoString: string | null | undefined
    ) {
        if (!isoString) {
            return "";
        }

        return DateTime.fromISO(isoString + "Z")
            .setZone(window.timezone)
            .toFormat("HH:mm");
    }

    static toUtcWithoutOffsetTime(isoString: string | null | undefined) {
        if (!isoString) {
            return "";
        }
        const convertedDateTime = DateTime.fromISO(isoString).setZone("utc");

        return convertedDateTime.toISO({ includeOffset: false });
    }
}
